import { useLocation, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Wrench,
  DollarSign,
  Menu,
  FileText,
  Settings,
  Plug,
  LogOut,
  Lock,
  CircleHelp,
  Sparkles,
} from "lucide-react";
import soluxLogo from "@/assets/solux-logo.png";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { getRoleLabel } from "@/hooks/useRolePermissions";
import { useTenantPlan, type FeatureKey } from "@/hooks/useTenantPlan";
import { FeatureLockedModal } from "@/components/billing/FeatureLockedModal";

type PermissionKey = 
  | "canViewDashboard" 
  | "canViewCRM" 
  | "canViewInstalacoes" 
  | "canViewFinanceiro" 
  | "canViewDocumentos" 
  | "canViewConfiguracoes" 
  | "canViewStatus"
  | "canViewIntegracoes";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ElementType;
  permission: PermissionKey;
  featureKey?: FeatureKey;
}

const mainItemsConfig: MenuItem[] = [
  { title: "Dashboard", url: "/app", icon: LayoutDashboard, permission: "canViewDashboard" },
  { title: "CRM", url: "/app/crm", icon: Users, permission: "canViewCRM", featureKey: "crm" },
  { title: "Logística", url: "/app/logistica", icon: Wrench, permission: "canViewInstalacoes", featureKey: "logistica" },
  { title: "Financeiro", url: "/app/financeiro", icon: DollarSign, permission: "canViewFinanceiro", featureKey: "financeiro" },
];

const moreItemsConfig: MenuItem[] = [
  { title: "Documentos", url: "/app/documentos", icon: FileText, permission: "canViewDocumentos" },
  { title: "Configurações", url: "/app/configuracoes", icon: Settings, permission: "canViewConfiguracoes" },
  { title: "Integrações", url: "/app/integracoes", icon: Plug, permission: "canViewIntegracoes" },
  { title: "Novidades", url: "/app/novidades", icon: Sparkles, permission: "canViewDashboard" },
  { title: "Ajuda", url: "/app/ajuda", icon: CircleHelp, permission: "canViewDashboard" },
];

export function BottomNav() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { profile, currentTenant, currentRole, permissions, signOut } = useAuth();
  const { hasFeature } = useTenantPlan();
  const [lockedModal, setLockedModal] = useState<{ open: boolean; featureKey: FeatureKey; label: string }>({
    open: false,
    featureKey: "crm",
    label: "",
  });

  const navigate = useNavigate();

  const isItemVisible = (item: MenuItem) => {
    if (!permissions[item.permission]) return false;
    return true;
  };

  const mainItems = useMemo(() => {
    return mainItemsConfig.filter(isItemVisible).slice(0, 4);
  }, [permissions, hasFeature]);

  const moreItems = useMemo(() => {
    const hiddenMainItems = mainItemsConfig.filter(
      (item) => isItemVisible(item) && !mainItems.includes(item)
    );
    const visibleMoreItems = moreItemsConfig.filter(isItemVisible);
    return [...hiddenMainItems, ...visibleMoreItems];
  }, [permissions, hasFeature, mainItems]);

  const isActive = (url: string) => {
    if (url === "/app") {
      return location.pathname === "/app";
    }
    return location.pathname.startsWith(url);
  };

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 pb-safe md:hidden">
      <div className="flex h-16 items-center justify-around px-2">
        {mainItems.map((item) => {
          const isLocked = !!item.featureKey && !hasFeature(item.featureKey);

          if (isLocked) {
            return (
              <button
                key={item.url}
                onClick={() =>
                  setLockedModal({
                    open: true,
                    featureKey: item.featureKey!,
                    label: item.title,
                  })
                }
                className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-muted-foreground opacity-60"
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  <Lock className="absolute -top-1 -right-1.5 h-2.5 w-2.5 text-amber-500" />
                </div>
                <span className="truncate">{item.title}</span>
              </button>
            );
          }

          return (
            <Link
              key={item.url}
              to={item.url}
              className={cn(
                "flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs transition-colors",
                isActive(item.url)
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <item.icon
                className={cn(
                  "h-5 w-5",
                  isActive(item.url) && "text-primary"
                )}
              />
              <span className="truncate">{item.title}</span>
            </Link>
          );
        })}

        {/* More menu */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <button className="flex flex-1 flex-col items-center justify-center gap-1 py-2 text-xs text-muted-foreground hover:text-foreground">
              <Menu className="h-5 w-5" />
              <span>Mais</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-xl pb-safe">
            <SheetHeader className="text-left">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg">
                  <img src={soluxLogo} alt="Solux CRM" className="h-8 w-8 object-contain" />
                </div>
                <div>
                  <SheetTitle>Solux CRM</SheetTitle>
                  <p className="text-sm text-muted-foreground">
                    {currentTenant?.name || "Selecione empresa"}
                  </p>
                </div>
              </div>
            </SheetHeader>

            <Separator className="my-4" />

            <div className="grid gap-2">
              {moreItems.map((item) => {
                const isLocked = !!item.featureKey && !hasFeature(item.featureKey);

                if (isLocked) {
                  return (
                    <button
                      key={item.url}
                      onClick={() => {
                        setIsOpen(false);
                        setLockedModal({
                          open: true,
                          featureKey: item.featureKey!,
                          label: item.title,
                        });
                      }}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm opacity-60"
                    >
                      <item.icon className="h-5 w-5" />
                      <span>{item.title}</span>
                      <Lock className="ml-auto h-3.5 w-3.5 text-amber-500" />
                    </button>
                  );
                }

                return (
                  <Link
                    key={item.url}
                    to={item.url}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors",
                      isActive(item.url)
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-muted"
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.title}</span>
                  </Link>
                );
              })}
            </div>

            <Separator className="my-4" />

            {/* User section */}
            <div className="flex items-center gap-3 rounded-lg bg-muted p-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={profile?.avatar_url || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary">
                  {getInitials(profile?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {profile?.full_name || "Usuário"}
                </p>
                <Badge variant="secondary" className="mt-1 text-[10px]">
                  {getRoleLabel(currentRole)}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  signOut();
                  setIsOpen(false);
                }}
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>

    <FeatureLockedModal
      open={lockedModal.open}
      onOpenChange={(open) => setLockedModal((prev) => ({ ...prev, open }))}
      featureKey={lockedModal.featureKey}
      featureLabel={lockedModal.label}
    />
    </>
  );
}
